﻿import { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import CompanySelect from '@/components/common/CompanySelect';
import CustomerSelect from '@/components/common/CustomerSelect';
import FilterChips from '@/components/common/FilterChips';
import { useCompanies } from '@/hooks/useCompanies';

export interface Filters {
  startDate: string;
  endDate: string;
  codazienda: string;
  codcliente: string;
}

interface Props {
  onSearch: (filters: Filters) => void;
  onReset: () => void;
}

const EMPTY: Filters = { startDate: '', endDate: '', codazienda: '0', codcliente: '0' };

export default function VisitFilters({ onSearch, onReset }: Props) {
  const { t } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [codazienda, setCodazienda] = useState('0');
  const [codcliente, setCodcliente] = useState('0');
  const [clienteLabel, setClienteLabel] = useState('');
  const { companies } = useCompanies();
  const isFirstRender = useRef(true);

  // Auto-search when any filter changes (skip first render)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    onSearch({ startDate, endDate, codazienda, codcliente });
  }, [startDate, endDate, codazienda, codcliente]);

  const getAziendaLabel = () => {
    const c = companies.find(c => c.codice === codazienda);
    return c ? c.descrizione : codazienda;
  };

  // Build chips from active filters
  const chips = [];
  if (startDate) chips.push({ key: 'startDate', label: 'Da', value: startDate, displayValue: startDate });
  if (endDate) chips.push({ key: 'endDate', label: 'A', value: endDate, displayValue: endDate });
  if (codazienda !== '0') chips.push({ key: 'codazienda', label: t('labels.company'), value: codazienda, displayValue: getAziendaLabel() });
  if (codcliente !== '0') chips.push({ key: 'codcliente', label: t('labels.customer'), value: codcliente, displayValue: clienteLabel || codcliente });

  const handleRemove = (key: string) => {
    if (key === 'startDate') setStartDate('');
    if (key === 'endDate') setEndDate('');
    if (key === 'codazienda') { setCodazienda('0'); setCodcliente('0'); setClienteLabel(''); }
    if (key === 'codcliente') { setCodcliente('0'); setClienteLabel(''); }
  };

  const handleClearAll = () => {
    setStartDate('');
    setEndDate('');
    setCodazienda('0');
    setCodcliente('0');
    setClienteLabel('');
    onReset();
  };

  return (
    <>
      <Card className="mb-2 card-filters">
        <Card.Body className="py-2">
          <Row className="align-items-end g-2">
            <Col sm={6} md={2}>
              <Form.Label className="filter-label mb-1">{t('labels.date')}</Form.Label>
              <Form.Control type="date" size="sm" value={startDate}
                onChange={e => setStartDate(e.target.value)} />
            </Col>
            <Col sm={6} md={2}>
              <Form.Label className="filter-label mb-1">&nbsp;</Form.Label>
              <Form.Control type="date" size="sm" value={endDate}
                onChange={e => setEndDate(e.target.value)} />
            </Col>
            <Col sm={6} md={4}>
              <Form.Label className="filter-label mb-1">{t('labels.company')}</Form.Label>
              <CompanySelect value={codazienda}
                onChange={v => { setCodazienda(v); setCodcliente('0'); setClienteLabel(''); }} />
            </Col>
            <Col sm={6} md={4}>
              <Form.Label className="filter-label mb-1">{t('labels.customer')}</Form.Label>
              <CustomerSelect codazienda={codazienda} value={codcliente}
                onChange={(v) => setCodcliente(v)} />
            </Col>
          </Row>
        </Card.Body>
        {chips.length > 0 && (
          <Card.Footer className="py-2">
            <FilterChips chips={chips} onRemove={handleRemove} onClearAll={handleClearAll} />
          </Card.Footer>
        )}
      </Card>
    </>
  );
}


